const API_BASE = '/api'
const TOKEN_STORAGE_KEY = '@freedomcert_token'
const ADMIN_TOKEN_STORAGE_KEY = '@freedomcert_admin_token'

export function getToken(): string | null {
  return localStorage.getItem(TOKEN_STORAGE_KEY)
}

export function setToken(token: string | null): void {
  if (token) {
    localStorage.setItem(TOKEN_STORAGE_KEY, token)
  } else {
    localStorage.removeItem(TOKEN_STORAGE_KEY)
  }
}

export function getAdminToken(): string | null {
  return localStorage.getItem(ADMIN_TOKEN_STORAGE_KEY)
}

export function setAdminToken(token: string | null): void {
  if (token) {
    localStorage.setItem(ADMIN_TOKEN_STORAGE_KEY, token)
  } else {
    localStorage.removeItem(ADMIN_TOKEN_STORAGE_KEY)
  }
}

export function isImpersonating(): boolean {
  return getAdminToken() !== null
}

export function stopImpersonation(): void {
  const adminToken = getAdminToken()
  if (adminToken) {
    setToken(adminToken)
    setAdminToken(null)
  }
}

export function getAuthHeaders(): Record<string, string> {
  const token = getToken()
  const headers: Record<string, string> = {}
  if (token) {
    headers['Authorization'] = `Bearer ${token}`
  }
  return headers
}

export class ApiError extends Error {
  isDuplicate: boolean
  constructor(public status: number, message: string, isDuplicate = false) {
    super(message)
    this.name = 'ApiError'
    this.isDuplicate = isDuplicate
  }
}

async function handleResponse<T>(response: Response): Promise<T> {
  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Request failed' }))
    throw new ApiError(response.status, error.message || 'Request failed', error.duplicate === true)
  }
  
  // Handle empty responses
  const text = await response.text()
  if (!text) {
    return {} as T
  }
  
  try {
    return JSON.parse(text) as T
  } catch (e) {
    console.error('Failed to parse response as JSON:', text.substring(0, 200))
    throw new ApiError(response.status, 'Invalid response format')
  }
}

export async function apiGet<T>(endpoint: string): Promise<T> {
  const response = await fetch(`${API_BASE}${endpoint}`, {
    headers: getAuthHeaders(),
    credentials: 'include',
  })
  return handleResponse<T>(response)
}

export async function apiPost<T>(endpoint: string, data?: unknown): Promise<T> {
  const response = await fetch(`${API_BASE}${endpoint}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...getAuthHeaders(),
    },
    credentials: 'include',
    body: data ? JSON.stringify(data) : undefined,
  })
  return handleResponse<T>(response)
}

export async function apiPut<T>(endpoint: string, data?: unknown): Promise<T> {
  const response = await fetch(`${API_BASE}${endpoint}`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      ...getAuthHeaders(),
    },
    credentials: 'include',
    body: data ? JSON.stringify(data) : undefined,
  })
  return handleResponse<T>(response)
}

export async function apiPatch<T>(endpoint: string, data?: unknown): Promise<T> {
  const response = await fetch(`${API_BASE}${endpoint}`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      ...getAuthHeaders(),
    },
    credentials: 'include',
    body: data ? JSON.stringify(data) : undefined,
  })
  return handleResponse<T>(response)
}

export async function apiDelete<T>(endpoint: string): Promise<T> {
  const response = await fetch(`${API_BASE}${endpoint}`, {
    method: 'DELETE',
    headers: getAuthHeaders(),
    credentials: 'include',
  })
  return handleResponse<T>(response)
}
